import { useState } from "react";
import { Plus, Info, ChevronDown, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HELP = "Organizing more than one business? “New Idea” opens a fresh, blank organizer for another business or concept. Each idea keeps its own fields, logo, copilot chat and PDF — switch between them any time.";

export default function IdeaSwitcher({ ideas, active, onSwitch, onCreated, onRenamed, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = ideas.find((i) => i.idea_id === active) || ideas[0];

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/organizer/ideas", { name: name.trim() || "New idea" });
      onCreated(data.idea);
      setCreating(false);
      setName("");
      toast.success(`“${data.idea.name}” started — a blank organizer just for this idea.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/organizer/ideas/${current.idea_id}`, { name: name.trim() });
      onRenamed(current.idea_id, name.trim());
      setRenaming(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (idea) => {
    if (!window.confirm(`Delete “${idea.name}”? Its fields, logo and copilot chat will be removed for good.`)) return;
    try {
      await api.delete(`/organizer/ideas/${idea.idea_id}`);
      onDeleted(idea.idea_id);
      toast.success("Idea deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="idea-switcher">
      <div className="relative">
        <button onClick={() => setOpen((o) => !o)} data-testid="idea-menu-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#E8DCCB] bg-[#FFFDF7] px-4 py-2 text-sm font-semibold hover:border-[#A8785A] transition-colors max-w-[260px]">
          <span className="truncate" data-testid="idea-current-name">{current?.name}</span>
          <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-30 mt-2 w-72 glass rounded-2xl p-2 shadow-[0_16px_48px_rgba(58,46,37,0.14)]" data-testid="idea-menu">
            <p className="eyebrow px-3 pt-2 pb-1">Your ideas · {ideas.length}</p>
            {ideas.map((i) => (
              <div key={i.idea_id} className={`flex items-center gap-2 rounded-xl px-3 py-2 ${i.idea_id === active ? "bg-[#FFF3F8]" : "hover:bg-[#FAF6EF]"}`}>
                <button onClick={() => { onSwitch(i.idea_id); setOpen(false); }} className="flex-1 text-left text-sm truncate" data-testid={`idea-option-${i.idea_id}`}>
                  {i.idea_id === active && <Check size={12} className="inline mr-1.5 text-[#A8785A]" />}{i.name}
                </button>
                {ideas.length > 1 && (
                  <button onClick={() => remove(i)} className="text-[#B9A896] hover:text-[#C48A9F]" aria-label="Delete idea" data-testid={`idea-delete-${i.idea_id}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {renaming ? (
        <form onSubmit={rename} className="flex items-center gap-1.5">
          <input autoFocus className="field !py-1.5 !px-3 !text-sm w-44" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="idea-rename-input" />
          <button type="submit" disabled={busy} className="w-8 h-8 rounded-full bg-[#A8785A] text-[#FFFDF7] flex items-center justify-center" data-testid="idea-rename-save"><Check size={14} /></button>
          <button type="button" onClick={() => setRenaming(false)} className="w-8 h-8 rounded-full border border-[#E8DCCB] flex items-center justify-center text-[#8C7562]"><X size={14} /></button>
        </form>
      ) : (
        <button onClick={() => { setName(current?.name || ""); setRenaming(true); }} className="w-9 h-9 rounded-full border border-[#E8DCCB] bg-[#FFFDF7] flex items-center justify-center text-[#8C7562] hover:text-[#3A2E25] hover:border-[#A8785A] transition-colors" title="Rename this idea" aria-label="Rename this idea" data-testid="idea-rename-btn">
          <Pencil size={13} />
        </button>
      )}

      <div className="relative flex items-center gap-1.5">
        {creating ? (
          <form onSubmit={create} className="flex items-center gap-1.5" data-testid="idea-create-form">
            <input autoFocus className="field !py-1.5 !px-3 !text-sm w-48" placeholder="Name your new idea…" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="idea-create-input" />
            <button type="submit" disabled={busy} className="btn-primary !py-1.5 !px-3.5" data-testid="idea-create-save">{busy ? "…" : "Create"}</button>
            <button type="button" onClick={() => setCreating(false)} className="w-8 h-8 rounded-full border border-[#E8DCCB] flex items-center justify-center text-[#8C7562]"><X size={14} /></button>
          </form>
        ) : (
          <button onClick={() => { setName(""); setCreating(true); }} title={HELP} className="btn-primary !py-2 !px-4" data-testid="new-idea-btn">
            <Plus size={14} /> New Idea
          </button>
        )}
        <button onClick={() => setHelp((h) => !h)} aria-label="What does New Idea do?" data-testid="new-idea-info-btn"
          className="w-7 h-7 rounded-full border border-[#D4A0B4] text-[#C48A9F] flex items-center justify-center hover:bg-[#FFF3F8] transition-colors">
          <Info size={13} />
        </button>
        {help && (
          <div className="absolute right-0 top-full mt-2 w-72 z-30 glass rounded-2xl p-4 text-xs leading-relaxed text-[#57473B] shadow-[0_16px_48px_rgba(58,46,37,0.14)]" data-testid="new-idea-help">
            <p className="eyebrow mb-2">New Idea</p>
            {HELP}
            <button onClick={() => setHelp(false)} className="block mt-3 text-[11px] font-semibold text-[#A8785A]">Got it</button>
          </div>
        )}
      </div>
    </div>
  );
}
